import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Grid, Typography, Modal, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Lottie from 'lottie-react';
import { formatPeriod } from '../Components/utilityFunctions';
import loadingAnimation from '../static/loadingAnimationLottie.json';
import MonthlyCostStatusSnapshotViewer from '../Components/ProjectViewComponents/MonthlyCostStatusSnapshotModalViewer';
import ChipComponent from '../Components/PortfolioViewComponents/ChipComponent';
import StageChip from '../Components/PortfolioViewComponents/StageChip';

import { setSortField } from '../../../store/slices/portfolioSlice';
import { setViewMonthlyCostStatusSubmission } from '../../../store/slices/projectSlice';
import { format } from 'date-fns';

// PHD primary navy — matches the modal header and existing group header color
const PHD_HEADER_BG = '#062b45';
const PHD_HEADER_SUB_BG = '#093d61';
const WARRANTY_STAGE = 'Warranty';

// Map DataGrid field → server-side sort field value used by the API.
// Only fields that the API can sort are listed here; everything else sorts client-side.
const SERVER_SORT_MAP = {
	name: { asc: 'project_name', desc: '-project_name' },
	account: { asc: 'account_name', desc: '-account_name' },
	stage: { asc: 'stage', desc: '-stage' },
	project_type: { asc: 'project_type', desc: '-project_type' },
	cost_status: { asc: 'cost_status', desc: '-cost_status' },
	projected_contract: { asc: 'projected_contract', desc: '-projected_contract' },
	projected_contract_change: { asc: 'projected_contract_change', desc: '-projected_contract_change' },
	projected_contract_change_percent: { asc: 'projected_contract_change_percent', desc: '-projected_contract_change_percent' },
	profit_percent: { asc: 'profit_percent', desc: '-profit_percent' },
	profit_change_percent: { asc: 'profit_change_percent', desc: '-profit_change_percent' },
	contingency: { asc: 'contingency', desc: '-contingency' },
	contingency_change: { asc: 'contingency_change', desc: '-contingency_change' },
	contingency_change_percent: { asc: 'contingency_change_percent', desc: '-contingency_change_percent' },
	start_date: { asc: 'actual_start_date', desc: '-actual_start_date' },
	end_date: { asc: 'projected_finish_date', desc: '-projected_finish_date' },
};

const CHANGE_FIELDS = new Set([
	'projected_contract_change',
	'projected_contract_change_percent',
	'profit_change_percent',
	'contingency_change_percent',
	'contingency_change',
]);

function formatMoney(value) {
	if (typeof value !== 'number') return value;
	return '$' + value.toLocaleString('en-US');
}

function formatPercent(value, decimals = 2) {
	if (typeof value !== 'number') return value;
	return `${value.toFixed(decimals)}%`;
}

function formatDate(value) {
	if (!value) return '--';
	// Parse YYYY-MM-DD as a LOCAL date so the displayed day doesn't shift in
	// negative-UTC timezones (where `new Date('2026-12-31')` becomes 12/30).
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		const [y, m, d] = value.split('-').map(Number);
		return new Date(y, m - 1, d).toLocaleDateString('en-US');
	}
	return new Date(value).toLocaleDateString('en-US');
}

function createRow(project) {
	return {
		id: project.id,
		account: project.account_name || '--',
		arco_id: project.arco_id,
		name: `${project.arco_id ? `${project.arco_id} | ` : ''}${project.name} | ${project.project_number}`,
		project_type: project.project_type,
		stage: project.stage,
		cost_status: project.cost_status || '--',
		projected_contract: project.projected_contract ?? '--',
		projected_contract_change: project.projected_contract_change ?? '--',
		projected_contract_change_percent: project.projected_contract_change_percent ?? '--',
		profit_percent: project.profit_percent ?? '--',
		profit_change_percent: project.profit_change_percent ?? '--',
		contingency: project.contingency ?? '--',
		contingency_change: project.contingency_change ?? '--',
		contingency_change_percent: project.contingency_change_percent ?? '--',
		start_date: project.actual_start_date,
		end_date: project.projected_finish_date,
		month: project.month,
		last_updated_at: project.last_updated_at
			? new Date(project.last_updated_at).toLocaleString('en-US')
			: '--',
		last_updated_by: project.last_updated_by || '--',
		is_late_submission: project.is_late_submission,
	};
}

// Cost Status filter options
const COST_STATUS_OPTIONS = ['Not Required', 'Not Started', 'Draft', 'Submitted', 'Submitted Late'];

const PortfolioDashboard = () => {
	const dispatch = useDispatch();
	const hubDomain = useSelector((state) => state.global.hubDomain);
	const viewMonthlyCostStatusSubmission = useSelector(
		(state) => state.project.viewMonthlyCostStatusSubmission
	);
	const { portfolioProjects, portfolioError, sortField, selectedMonth, portfolioSearchTerm, selectedPortfolioCompany } = useSelector(
		(state) => state.portfolio
	);
	const isLoading = useSelector((state) => state.global.isLoading);

	const firstOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
	const currentMonth = format(firstOfCurrentMonth, 'yyyy-MM');

	// =========================================================
	// COST STATUS VIEWER
	// =========================================================
	const [costStatusViewerParams, setCostStatusViewerParams] = useState({
		projectId: null,
		month: null,
		mode: null,
	});

	const handleOpenCostStatusViewer = (projectId, month) => {
		setCostStatusViewerParams({
			projectId,
			month,
			mode: month !== currentMonth ? 'older' : 'latest',
		});
		dispatch(setViewMonthlyCostStatusSubmission(true));
	};

	// =========================================================
	// COST STATUS CLIENT FILTER
	// =========================================================
	const searchTerm = portfolioSearchTerm;
	const [costStatusMenuFilter, setCostStatusMenuFilter] = useState(() => {
		const saved = sessionStorage.getItem('costStatusMenuFilter');
		return saved ? JSON.parse(saved) : COST_STATUS_OPTIONS;
	});

	// Persist cost status filter to session storage whenever it changes
	const updateCostStatusFilter = (val) => {
		setCostStatusMenuFilter((prev) => {
			const updated = prev.includes(val)
				? prev.filter((item) => item !== val)
				: [...prev, val];
			sessionStorage.setItem('costStatusMenuFilter', JSON.stringify(updated));
			return updated;
		});
	};

	// =========================================================
	// COLUMN VISIBILITY — DataGrid manages this internally via columnVisibilityModel
	// Seed it from sessionStorage so the user's preferences survive a page refresh.
	// =========================================================
	const hiddenByDefault = ['project_type', 'contingency_change', 'projected_contract_change'];
	const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
		const saved = sessionStorage.getItem('columnVisibilityModel');
		if (saved) return JSON.parse(saved);
		return Object.fromEntries(hiddenByDefault.map((id) => [id, false]));
	});

	const handleColumnVisibilityModelChange = (newModel) => {
		sessionStorage.setItem('columnVisibilityModel', JSON.stringify(newModel));
		setColumnVisibilityModel(newModel);
	};

	// =========================================================
	// ROWS
	// =========================================================
	const allRows = useMemo(
		() => portfolioProjects.map((p) => createRow(p)),
		[portfolioProjects]
	);

	// =========================================================
	// SORT MODEL
	// DataGrid's sort model drives column-header sort indicators.
	// Server-mapped columns sync to Redux sortField (drives re-fetch);
	// non-mapped columns sort locally via localSortModel state.
	// =========================================================
	const [localSortModel, setLocalSortModel] = useState([]);

	const derivedSortModel = useMemo(() => {
		if (localSortModel.length > 0) return localSortModel;
		const serverEntry = Object.entries(SERVER_SORT_MAP).find(
			([, map]) => map.asc === sortField || map.desc === sortField
		);
		if (!serverEntry) return [];
		const [field, map] = serverEntry;
		return [{ field, sort: map.desc === sortField ? 'desc' : 'asc' }];
	}, [sortField, localSortModel]);

	const handleSortModelChange = (model) => {
		if (model.length === 0) {
			setLocalSortModel([]);
			return;
		}
		const { field, sort } = model[0];
		const serverMap = SERVER_SORT_MAP[field];
		if (serverMap) {
			// Server-mapped — push to Redux, clear local override
			dispatch(setSortField(serverMap[sort] ?? serverMap.asc));
			setLocalSortModel([]);
		} else {
			// Client-only sort — drive DataGrid via local state
			setLocalSortModel(model);
		}
	};

	// =========================================================
	// FILTERED + WARRANTY-PINNED ROWS
	// DataGrid Community doesn't have pinned rows — we handle it by appending
	// Warranty rows after non-warranty rows in the data array.
	// =========================================================
	const displayRows = useMemo(() => {
		const lowerSearch = searchTerm.toLowerCase();

		const filtered = allRows.filter(
			(row) =>
				costStatusMenuFilter.includes(row.cost_status) &&
				row.name.toLowerCase().includes(lowerSearch)
		);

		const normal = filtered.filter((r) => r.stage !== WARRANTY_STAGE);
		const warranty = filtered.filter((r) => r.stage === WARRANTY_STAGE);
		return [...normal, ...warranty];
	}, [allRows, costStatusMenuFilter, searchTerm]);

	// Distinct, sorted values per column for auto-populated dropdown filters
	// (ENG-898). Standard categorical columns get the `singleSelect` filter UI
	// so users pick from real values instead of typing free text.
	const uniqueValuesByField = useMemo(() => {
		const collect = (field) => {
			const seen = new Set();
			for (const r of allRows) {
				const v = r[field];
				if (v !== undefined && v !== null && v !== '--') seen.add(v);
			}
			return Array.from(seen).sort();
		};
		return {
			account: collect('account'),
			project_type: collect('project_type'),
			stage: collect('stage'),
			cost_status: collect('cost_status'),
		};
	}, [allRows]);

	// =========================================================
	// DATAGRID COLUMN DEFINITIONS
	// =========================================================
	const columns = useMemo(
		() => [
			{
				field: 'name',
				headerName: 'Project',
				minWidth: 200,
				flex: 2,
				sortable: true,
				renderCell: ({ value, row }) => (
					<Tooltip
						title={
							row.cost_status !== 'Not Required' &&
							row.cost_status !== 'Not Started' &&
							selectedMonth === 'Latest' &&
							row.month !== currentMonth
								? `Data from ${row.month} cost status`
								: ''
						}
						placement="right"
					>
						<Box
							component="span"
							onClick={() =>
								window.open(
									`${hubDomain}/phd/?tab=project&project_id=${row.id}`,
									'_blank'
								)
							}
							sx={{
								color: '#0d47a1',
								cursor: 'pointer',
								'&:hover': { textDecoration: 'underline' },
							}}
						>
							{value}
						</Box>
					</Tooltip>
				),
			},
			{
				field: 'account',
				headerName: 'Client',
				minWidth: 110,
				flex: 1,
				sortable: true,
				type: 'singleSelect',
				valueOptions: uniqueValuesByField.account,
			},
			{
				field: 'project_type',
				headerName: 'Project Type',
				minWidth: 140,
				flex: 1,
				sortable: true,
				type: 'singleSelect',
				valueOptions: uniqueValuesByField.project_type,
			},
			{
				field: 'stage',
				headerName: 'Stage',
				minWidth: 110,
				flex: 1,
				sortable: true,
				type: 'singleSelect',
				valueOptions: uniqueValuesByField.stage,
				renderCell: ({ value }) => <StageChip value={value} />,
			},
			{
				field: 'cost_status',
				headerName: 'Cost Status',
				minWidth: 140,
				flex: 1,
				sortable: true,
				type: 'singleSelect',
				valueOptions: uniqueValuesByField.cost_status,
				renderCell: ({ value, row }) => (
					<ChipComponent
						value={value}
						row={row}
						handleOpenCostStatusViewer={handleOpenCostStatusViewer}
					/>
				),
			},
			{
				field: 'projected_contract',
				headerName: 'Contract Amount',
				minWidth: 150,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatMoney(value),
			},
			{
				field: 'projected_contract_change',
				headerName: 'Contract Chg ($)',
				minWidth: 150,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatMoney(value),
				cellClassName: ({ value }) =>
					typeof value === 'number'
						? value > 0
							? 'change-positive'
							: value < 0
							? 'change-negative'
							: ''
						: '',
			},
			{
				field: 'projected_contract_change_percent',
				headerName: 'Contract Chg (%)',
				minWidth: 145,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatPercent(value),
				cellClassName: ({ value }) =>
					typeof value === 'number'
						? value > 0
							? 'change-positive'
							: value < 0
							? 'change-negative'
							: ''
						: '',
			},
			{
				field: 'profit_percent',
				headerName: 'Profit',
				minWidth: 90,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatPercent(value, 2),
			},
			{
				field: 'profit_change_percent',
				headerName: 'Profit Chg (%)',
				minWidth: 130,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatPercent(value, 2),
				cellClassName: ({ value }) =>
					typeof value === 'number'
						? value > 0
							? 'change-positive'
							: value < 0
							? 'change-negative'
							: ''
						: '',
			},
			{
				field: 'contingency',
				headerName: 'Contingency',
				minWidth: 125,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatMoney(value),
			},
			{
				field: 'contingency_change',
				headerName: 'Contingency Chg',
				minWidth: 155,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatMoney(value),
				cellClassName: ({ value }) =>
					typeof value === 'number'
						? value > 0
							? 'change-positive'
							: value < 0
							? 'change-negative'
							: ''
						: '',
			},
			{
				field: 'contingency_change_percent',
				headerName: 'Contingency Chg (%)',
				minWidth: 175,
				flex: 1,
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				sortable: true,
				valueFormatter: (value) => formatPercent(value),
				cellClassName: ({ value }) =>
					typeof value === 'number'
						? value > 0
							? 'change-positive'
							: value < 0
							? 'change-negative'
							: ''
						: '',
			},
			{
				field: 'start_date',
				headerName: 'Start Date',
				minWidth: 110,
				flex: 1,
				sortable: true,
				valueFormatter: (value) => formatDate(value),
			},
			{
				field: 'end_date',
				headerName: 'Finish Date',
				minWidth: 115,
				flex: 1,
				sortable: true,
				valueFormatter: (value) => formatDate(value),
			},
		],
		[hubDomain, currentMonth, selectedMonth, uniqueValuesByField]
	);

	// =========================================================
	// LOADING / EMPTY STATE
	// =========================================================
	if (isLoading || !portfolioProjects || portfolioProjects.length === 0) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					minHeight: '100vh',
					alignItems: 'center',
					justifyContent: 'center',
					p: 2,
				}}
			>
				<Grid
					container
					spacing={2}
					sx={{
						mt: 2,
						height: '90vh',
						width: '98vw',
						maxWidth: '100%',
						mx: 'auto',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Grid item xs={12} sx={{ textAlign: 'center' }}>
						<Typography variant="h6" align="center" sx={{ mb: 3 }}>
							{isLoading
								? 'Fetching projects...'
								: 'No projects found for the selected filters.'}
						</Typography>
						{isLoading && (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									width: '100%',
									height: '25vh',
								}}
							>
								<Lottie
									animationData={loadingAnimation}
									loop
									autoplay
									style={{ width: 'auto', height: '100%', maxWidth: '400px' }}
								/>
							</Box>
						)}
						{portfolioError && (
							<Typography sx={{ color: 'error.main', mb: 2, textAlign: 'center', mt: 2 }}>
								{portfolioError}
							</Typography>
						)}
					</Grid>
				</Grid>
			</Box>
		);
	}

	// =========================================================
	// RENDER
	// =========================================================
	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				p: 1,
				boxSizing: 'border-box',
				overflow: 'hidden',
			}}
		>
			{/* Period label + CSR link — sits below the header filter strip */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexShrink: 0, gap: 2 }}>
				<Typography
					variant="body2"
					sx={{ color: 'text.secondary', fontStyle: 'italic' }}
				>
					{selectedMonth && selectedMonth !== 'Latest'
						? `Viewing Cost Status for ${formatPeriod(selectedMonth)}`
						: "Viewing each project's most recent Cost Status"}
				</Typography>
				{selectedPortfolioCompany && selectedPortfolioCompany.length > 0 && (
					<Box
						component="a"
						href={`https://hub.arcomurray.com/reports/confidentiality/view/?company=${selectedPortfolioCompany}`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.5,
							fontSize: '0.8rem',
							color: 'primary.main',
							textDecoration: 'none',
							'&:hover': { textDecoration: 'underline' },
						}}
					>
						Confidentiality Sales Report
						<OpenInNewIcon sx={{ fontSize: '0.95rem' }} />
					</Box>
				)}
			</Box>

			{/* DataGrid — fills remaining height, no extra wrapper scrollbar */}
			<Box sx={{ flex: '1 1 0', minHeight: 0, width: '100%', overflow: 'hidden' }}>
				<DataGrid
					rows={displayRows}
					columns={columns}
					sortingMode="client"
					sortModel={derivedSortModel}
					onSortModelChange={handleSortModelChange}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
					density="compact"
					columnHeaderHeight={72}
					disableRowSelectionOnClick
					pageSizeOptions={[25, 50, 100]}
					initialState={{
						pagination: { paginationModel: { pageSize: 25 } },
					}}
					slots={{
						columnMenuIcon: MoreVertIcon,
					}}
					sx={{
						height: '100%',
						borderRadius: '10px',
						// Font — match project-wide Titillium Web
						fontFamily: '"Titillium Web", sans-serif',
						fontSize: '0.875rem',
						'& .MuiDataGrid-cell': {
							fontFamily: '"Titillium Web", sans-serif',
							fontSize: '0.875rem',
						},
						// Dark header
						'& .MuiDataGrid-columnHeaders': {
							backgroundColor: PHD_HEADER_BG,
							color: '#fff',
							textTransform: 'uppercase',
							fontSize: '0.75rem',
							fontWeight: 'bold',
						},
						'& .MuiDataGrid-columnHeader': {
							backgroundColor: PHD_HEADER_SUB_BG,
						},
						'& .MuiDataGrid-columnHeader:hover': {
							backgroundColor: '#0a4d7a',
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontFamily: '"Titillium Web", sans-serif',
							color: '#fff',
							fontWeight: 'bold',
							textTransform: 'uppercase',
							fontSize: '0.75rem',
							whiteSpace: 'normal',
							lineHeight: 1.2,
							overflowWrap: 'break-word',
							textOverflow: 'clip',
						},
						'& .MuiDataGrid-columnHeaderTitleContainerContent': {
							overflow: 'visible',
							whiteSpace: 'normal',
						},
						// Sort + menu icon buttons in column headers: force transparent
						// background and white glyph so they render as visible arrows
						// / dots on the navy header (default theme paints them white
						// because of dark mode contrast detection, hiding the SVG).
						'& .MuiDataGrid-sortButton, & .MuiDataGrid-menuIconButton': {
							backgroundColor: 'transparent !important',
							color: '#fff !important',
						},
						'& .MuiDataGrid-sortButton:hover, & .MuiDataGrid-menuIconButton:hover': {
							backgroundColor: 'rgba(255,255,255,0.12) !important',
						},
						'& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon svg': {
							color: '#fff !important',
							fill: '#fff !important',
						},
						// Always show the 3-dot column-menu icon, not just on hover,
						// to match the reference design.
						'& .MuiDataGrid-menuIcon': {
							visibility: 'visible',
							opacity: 1,
							width: 'auto',
						},
						// Striped rows
						'& .MuiDataGrid-row:nth-of-type(even)': {
							backgroundColor: '#f5f5f5',
						},
						'& .MuiDataGrid-row:nth-of-type(odd)': {
							backgroundColor: '#fff',
						},
						'& .MuiDataGrid-row:hover': {
							backgroundColor: '#ADD8E6 !important',
						},
						// Change column colors
						'& .change-positive': { color: 'green' },
						'& .change-negative': { color: 'red' },
					}}
				/>
			</Box>

			{/* Monthly cost status viewer modal */}
			<Modal
				open={viewMonthlyCostStatusSubmission}
				onClose={() => dispatch(setViewMonthlyCostStatusSubmission(false))}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '70vw',
						bgcolor: 'background.paper',
						boxShadow: 24,
						borderRadius: 2,
						maxHeight: '80vh',
						overflow: 'auto',
					}}
				>
					<Box sx={{ p: 3, color: 'white', backgroundColor: '#093d61' }}>
						<Grid container alignItems="center" justifyContent="space-between">
							<Grid item xs={9}>
								<Typography
									className="titillium-web-base"
									sx={{ fontSize: '25px', fontWeight: 'bold', mb: 0.625 }}
								>
									Monthly Cost Status Submission - Viewer
								</Typography>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ p: 3 }}>
						<MonthlyCostStatusSnapshotViewer
							projectId={costStatusViewerParams.projectId}
							mode={costStatusViewerParams.mode}
							month={costStatusViewerParams.month}
						/>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
};

export default PortfolioDashboard;
